import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  ExportParamsType,
  FilesType,
  PickerFilesType,
  PickerType,
} from "pages/pickers/types";
import {
  DetailPickerStateType,
  PickerWrongFileType,
} from "./types/detailPicker";

import { RootState } from "store";
import { PickerFileRequestType } from "pages/detailPicker/types";
import {
  AccountTypesType,
  BankType,
  PickerFileSaveParamsType,
} from "sagas/types/pickers";
import { ProvincesTypes } from "sagas/types/pickers";
import { MOTORIZED_VEHICLES_TAGS, VEHICLE_TAGS } from "utils/constants";
import { FormApi } from "final-form";

export const initialState: DetailPickerStateType = {
  dirty: false,
  nameDisplay: "",
  phoneNumber: "",
  invalidBank: true,
  bankNameRequested: false,
  banks: [],
  provinces: [],
  accountTypes: [],
  pendingUserAdminPicker: {
    id: 0,
    enable: false,
    registerDatetime: "",
    status: {
      description: "",
      tag: "",
    },
    region: { id: "", name: "" },
    desiredWorkzones: "",
    personalData: {
      name: "",
      surname: "",
      dateOfBirth: null,
      identification: null,
      email: "",
      phone: {
        areaNumber: "",
        countryNumber: "",
        number: "",
        registerDate: undefined,
      },
      comeFrom: "",
    },
    accountingData: {
      address: {
        id: 1,
        locality: "",
        postalCode: "",
        province: null,
        street: "",
        streetNumber: "",
      },
      companyName: "",
      fiscalNumber: "",
      sapInterlocutor: "",
      id: 0,
      account: {
        number: "",
        bank: {
          tag: "",
          name: "",
        },
        type: "",
      },
    },
    vehicle: {
      type: VEHICLE_TAGS.BICYCLE,
      active: false,
      approve: false,
      patent: "",
      expirationDateDriverLicense: null,
      expirationDateIdentificationVehicle: null,
      expirationDatePolicyVehicle: null,
    },
    ranking: {
      fullRanking: false,
      priority: 0,
    },
    files: {
      personalData: {
        status: "EMPTY",
        content: [],
      },
      accountingData: {
        status: "EMPTY",
        content: [],
      },
      vehicle: {
        status: "EMPTY",
        content: [],
      },
    },
  },
  fiscalNumberDuplicated: false,
  hasSaved: true,
  pickerTermsStatus: null,
};

const SLICE_NAME = "detailPicker";

const getErrorItem = (files: FilesType, tag: PickerFilesType) => {
  let section: keyof FilesType = "vehicle";
  if (tag === "dni-front" || tag === "dni-back" || tag === "user-face")
    section = "personalData";
  if (tag === "cbu-certificate" || tag === "cuit-certificate")
    section = "accountingData";

  const index = files[section].content.findIndex((f) => f.tag === tag);
  if (index >= 0)
    return {
      section,
      index,
    };

  return null;
};

export const detailPickerSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetPicker: (state: DetailPickerStateType) => {
      state.pendingUserAdminPicker = initialState.pendingUserAdminPicker;
    },
    getPendingUserPickerRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<number>
    ) => {},
    getPendingUserPickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      const { payload } = action;
      const { personalData } = payload;
      const { phone } = personalData;
      state.pendingUserAdminPicker = {
        ...payload,
      };
      state.invalidBank = !!!(payload.accountingData?.account?.bank as BankType)
        ?.name;
      state.nameDisplay = `${personalData.name} ${personalData.surname}`;
      state.phoneNumber =
        phone.areaNumber && phone.number
          ? `${phone.countryNumber}${phone.areaNumber}${phone.number}`
          : undefined;
    },
    getPendingUserPickerError: () => {},
    setDirty: (
      state: DetailPickerStateType,
      action: PayloadAction<boolean>
    ) => {
      state.dirty = action.payload;
    },
    setWrongFile: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerWrongFileType>
    ) => {
      const { tag, error } = action.payload;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          error;
      }
    },
    resetWrongFiles: (state: DetailPickerStateType) => {
      const { files } = state.pendingUserAdminPicker;
      state.pendingUserAdminPicker.files.personalData.content =
        files.personalData.content.map((f) => ({ ...f, error: "NONE" }));
      state.pendingUserAdminPicker.files.accountingData.content =
        files.accountingData.content.map((f) => ({ ...f, error: "NONE" }));
      state.pendingUserAdminPicker.files.vehicle.content =
        files.vehicle.content.map((f) => ({ ...f, error: "NONE" }));
    },
    getPendingUserPickerExportRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<ExportParamsType>
    ) => {},
    getPendingUserPickerExportSuccess: () => {},
    getPendingUserPickerExportError: () => {},
    getPendingUserPickerDocumentsEditRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<{
        params: PickerType;
        scrollTo: () => void;
        formRef: React.MutableRefObject<FormApi<object, object> | null>;
      }>
    ) => {},
    getPendingUserPickerDocumentsEditSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      state.pendingUserAdminPicker = {
        ...action.payload,
        files: state.pendingUserAdminPicker.files,
      };
      state.hasSaved = true;
    },
    getPendingUserPickerDocumentsEditError: () => {},
    getAprovePickerRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<{
        params: PickerType;
        goBack: Function;
        scrollTo: () => void;
      }>
    ) => {},
    getAprovePickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      state.pendingUserAdminPicker = action.payload;
    },
    getAprovePickerError: () => {},
    getEditPickerRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<{
        params: PickerType;
        scrollTo: () => void;
        formRef: React.MutableRefObject<FormApi<object, object> | null>;
      }>
    ) => {},
    getEditPickerSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerType>
    ) => {
      state.pendingUserAdminPicker = action.payload;
      state.hasSaved = true;
    },
    getEditPickerError: () => {},
    getBanksRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<void>
    ) => {
      state.invalidBank = false;
    },
    getBanksSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<BankType[]>
    ) => {
      state.banks = action.payload;
    },
    getBanksError: (state: DetailPickerStateType) => {
      state.invalidBank = true;
      state.banks = [];
    },
    getBankNameFetch: (
      //evitar el loader
      state: DetailPickerStateType,
      action: PayloadAction<{ cbuPrefix?: string }>
    ) => {
      state.invalidBank = false;
      state.bankNameRequested = true;
    },
    getBankNameSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<BankType>
    ) => {
      state.pendingUserAdminPicker.accountingData.account.bank = action.payload;
      state.bankNameRequested = false;
    },
    getBankNameError: (state: DetailPickerStateType) => {
      state.invalidBank = true;
      state.bankNameRequested = false;
      state.pendingUserAdminPicker.accountingData.account.bank = {
        tag: "",
        name: "",
      };
    },
    getPickerFileRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFileRequestType>
    ) => {},
    getPickerFileSuccess: () => {},
    getPickerFileError: () => {},
    getPickerFileSaveRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFileSaveParamsType>
    ) => {},
    getPickerFileSaveSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NONE";
      }
    },
    getPickerFileSaveError: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NETWORK";
      }
    },
    getPickerFileDeleteRequest: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFileRequestType>
    ) => {},
    getPickerFileDeleteSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NONE";
      }
    },
    getPickerFileDeleteError: (
      state: DetailPickerStateType,
      action: PayloadAction<PickerFilesType>
    ) => {
      const { payload: tag } = action;
      const item = getErrorItem(state.pendingUserAdminPicker.files, tag);
      if (item) {
        const { section, index } = item;
        state.pendingUserAdminPicker.files[section].content[index].error =
          "NETWORK";
      }
    },
    getProvincesRequest: () => {},
    getProvincesError: () => {},
    getProvincesSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<ProvincesTypes[]>
    ) => {
      const { payload } = action;
      state.provinces = payload;
    },

    getAccountTypeRequest: () => {},
    getAccountTypeError: () => {},
    getAccountTypeSuccess: (
      state: DetailPickerStateType,
      action: PayloadAction<AccountTypesType[]>
    ) => {
      const { payload } = action;
      state.accountTypes = payload;
    },
    resetAccountType: (state) => {
      state.accountTypes = [];
    },
    resetBanks: (state) => {
      state.banks = [];
    },
    resetProvinces: (state) => {
      state.provinces = [];
    },
    setFiscalDuplicated: (state, action: PayloadAction<boolean>) => {
      state.fiscalNumberDuplicated = action.payload;
    },
    setHasSaved: (state, action: PayloadAction<boolean>) => {
      state.hasSaved = action.payload;
    },
    setPickerTermsStatus: (
      state,
      action: PayloadAction<DetailPickerStateType["pickerTermsStatus"]>
    ) => {
      state.pickerTermsStatus = action.payload;
    },
  },
});

// Se exporta el reducer/slice para asociarlo en la creación del store
export default detailPickerSlice.reducer;

// Selector del slice "detailPicker"
export const detailPickerSelector = (state: RootState) => state.detailPicker;

// Has picker wrong files?
export const hasPickerWrongFilesSelector = createSelector(
  (state: RootState) => state.detailPicker,
  (picker) => {
    const { files } = picker.pendingUserAdminPicker;
    return !Object.values(files)
      .map((f) => f.content)
      .flat()
      .every((f) => !f.error || f.error === "NONE");
  }
);
export const hasPickerAllRequiredFilesLoadedSelector = createSelector(
  (state: RootState) => state.detailPicker,
  (picker) => {
    const personalData = picker.pendingUserAdminPicker.files.personalData;
    const accountingData = picker.pendingUserAdminPicker.files.accountingData;
    const vehicle = picker.pendingUserAdminPicker.files.vehicle;
    const type = picker.pendingUserAdminPicker.vehicle.type;
    const isAccountingFilesValid =
      accountingData.content.length !== 0 &&
      !!accountingData.content.find((f) => f.tag === "cbu-certificate")
        ?.isUpload;

    if (MOTORIZED_VEHICLES_TAGS.includes(type)) {
      return (
        personalData.status === "COMPLETED" &&
        isAccountingFilesValid &&
        vehicle.status === "COMPLETED"
      );
    }
    return personalData.status === "COMPLETED" && isAccountingFilesValid;
  }
);

// Se exportan todas las acciones
export const actions = detailPickerSlice.actions;
